import { Component, Inject, Input, Output, OnChanges, Optional, SimpleChanges, EventEmitter } from '@angular/core';
import { GridModalComponent } from '@app/core/components/grid/grid.decorators';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ModalService } from '@app/core/services/modal.service';
import { StateDialogContext, TT_STATE_DIALOG } from '@app/modal/state-modal/state-modal.component';

@GridModalComponent('orderpickview')
@Component({
    selector: 'tt-order-pick-view',
    templateUrl: './order-pick-view.component.html',
    styleUrls: ['./order-pick-view.component.css'],
})
export class OrderPickViewComponent implements OnChanges {
    @Input()
    public params?: OrderPickViewParams;

    public get?: OrderPickViewGet;

    @Output()
    public submit = new EventEmitter<ErrorMessageResponse>();

    public grid?: GridOptions;

    public orderlines: OrderlinePickToItem[] = [];

    public saving = false;

    constructor(
        private datatask: DataTaskService,
        private modal: ModalService,
        @Optional() @Inject(TT_STATE_DIALOG) stateContext: StateDialogContext,
    ) {
        this.initFromDialog(stateContext);
    }

    public async save() {
        if (!this.get) return;

        try {
            this.saving = true;

            const { errorcode, errormessage } = await this.saveOrderPickView(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.submit.emit({ errorcode, errormessage });
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
        }
    }

    private async loadOrderlines() {
        if (this.get) {
            this.orderlines = await this.getOrderlinePickTos({ order_internal_no: this.get.order_internal_no });
        }
    }

    private async loadGet() {
        if (!!this.params) {
            this.get = await this.getOrderPickView(this.params);
        }
    }

    private readGrid() {
        if (!this.grid) {
            this.grid = new Grid().setRememberId('w_orderpick_orderpick_sb_grid').setLoadDataFunction({ method: 3712, parameters: () => this.get });
        } else {
            this.grid.gridfunc?.read();
        }
    }

    private async getOrderlinePickTos(params: { order_internal_no: string }): Promise<OrderlinePickToItem[]> {
        return this.datatask.Post(3925, params);
    }

    private async getOrderPickView(params: OrderPickViewParams): Promise<OrderPickViewGet> {
        return (await this.datatask.Post(3711, params))[0];
    }

    private async saveOrderPickView(params: OrderPickViewGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3927, params))[0];
    }

    private async init() {
        await this.loadGet();
        this.loadOrderlines();
        this.readGrid();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.init();
        }
    }

    private async initFromDialog(state?: StateDialogContext) {
        if (!!state) {
            this.params = state.params as unknown as OrderPickViewParams;
            state.setSize({ width: 'min(180rem, 85vw)', height: '95vh' });
            await this.init();
            this.submit.subscribe({ next: (value: ErrorMessageResponse) => state.dialogRef.close(value) });

            // @ts-ignore
            state.setHeading(this.get?.webapge_heading ?? '');
        }
    }
}

export interface OrderPickViewParams {
    orderpick_keyno: string;
}

export interface OrderPickViewGet {
    orderpick_keyno: string;
    order_internal_no: string;
    order_order_no: string;
    prod_id: string;
    prod_name: string;
    stock_keyno: string;
    stockbatch_keyno: string;
    input_quantity: string;
    input_unit_name: string;
    stock_quantity: string;
    labour_name: string;
    reg_datetime: string;
    pickroutine_note: string;
    orderline_keyno_pick_to: string;
}

interface OrderlinePickToItem {
    orderline_keyno: string;
    orderline_name: string;
}
